import { useState, useEffect } from 'react';
import { Package, AlertTriangle } from 'lucide-react';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import toast from '@/hooks/useToast';

interface InventoryItem {
  id: string;
  name: string;
  sku: string;
  stock: number;
  price: number;
  category: { name: string };
}

export const AdminInventory = () => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [newStock, setNewStock] = useState('');

  const fetchItems = () => {
    setLoading(true);
    api.get(`/admin/inventory${lowStockOnly ? '?lowStock=true' : ''}`)
      .then(res => setItems(res.data.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchItems(); }, [lowStockOnly]);

  const updateStock = async (id: string) => {
    if (!newStock || isNaN(Number(newStock))) return;
    try {
      await api.put(`/admin/inventory/${id}/stock`, { stock: parseInt(newStock) });
      setItems(prev => prev.map(i => i.id === id ? { ...i, stock: parseInt(newStock) } : i));
      setEditing(null);
      toast.success('Stock updated!');
    } catch { toast.error('Could not update stock'); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-2xl text-gray-800">Inventory Management</h1>
        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-orange-600">
          <input type="checkbox" checked={lowStockOnly} onChange={e => setLowStockOnly(e.target.checked)} className="accent-orange-500" />
          <AlertTriangle className="h-4 w-4" /> Low Stock Only
        </label>
      </div>

      <div className="bg-white rounded-card shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Product', 'SKU', 'Category', 'Current Stock', 'Update Stock'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-gray-400 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? Array(10).fill(0).map((_, i) => (
              <tr key={i}><td colSpan={5} className="px-4 py-3"><div className="h-5 bg-gray-100 rounded animate-pulse" /></td></tr>
            )) : items.map(item => (
              <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="font-medium text-gray-800 truncate max-w-[200px]">{item.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">{item.sku}</td>
                <td className="px-4 py-3 text-gray-500">{item.category?.name}</td>
                <td className="px-4 py-3">
                  <span className={`font-bold ${item.stock === 0 ? 'text-error' : item.stock < 10 ? 'text-orange-500' : 'text-gray-800'}`}>
                    {item.stock}
                  </span>
                  {item.stock < 10 && <AlertTriangle className="inline h-3.5 w-3.5 ml-1 text-orange-400" />}
                </td>
                <td className="px-4 py-3">
                  {editing === item.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={newStock}
                        onChange={e => setNewStock(e.target.value)}
                        className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                        autoFocus
                        min="0"
                      />
                      <Button size="sm" onClick={() => updateStock(item.id)}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => { setEditing(item.id); setNewStock(String(item.stock)); }}>
                      Update
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
